import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { SysState } from "../reducers/sysReducer";

// Keeping this timestamp in the Redux Store would require far too many state mutations,
// especially if we start to update the timestamp on mousemove. So instead we use a "private"
// variable here which is only accessible to other files through the exported getter function.

let lastUserInteractionTimestamp: number = Date.now();

export const getLastUserInteractionTimestamp = () =>
  lastUserInteractionTimestamp;

const listenerEventTypes = ["click", "keydown", "touchstart"];

const useInteractionListener = () => {
  const debugInteractionListener = useSelector(
    ({ sys }: { sys: SysState }) => sys.features.debugInteractionListener
  );

  useEffect(() => {
    if (debugInteractionListener)
      console.debug(
        `Creating Interaction Listeners for ${listenerEventTypes.join(",")}`
      );
    // Create Listeners on mount
    const listener = (ev: Event) => {
      if (debugInteractionListener)
        console.debug(
          `Interaction Detected (${ev.constructor.name}.${ev.type})`
        );
      // Update timestamp on event
      lastUserInteractionTimestamp = Date.now();
    };

    listenerEventTypes.forEach((type) =>
      window.addEventListener(type, listener, { passive: true })
    );

    document.addEventListener("visibilitychange", listener, { passive: true });

    return () => {
      if (debugInteractionListener)
        console.debug(
          `Deleting Interaction Listeners for ${listenerEventTypes.join(",")}`
        );
      // Delete Listeners on unmount
      listenerEventTypes.forEach((type) =>
        window.removeEventListener(type, listener)
      );
      document.removeEventListener("visibilitychange", listener);
    };
  }, [debugInteractionListener]);
};

export default useInteractionListener;

export const usePerformAfterInactivity = (
  callback: Function,
  inactivityMs: number
) => {
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  // Automatically reset the form after a certain period of time
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      if (Date.now() - lastUserInteractionTimestamp >= inactivityMs) {
        callback();
      }
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);
};

// WARNING: The following exports are only meant for use in tests.

export const setLastUserInteractionTimestamp = (timestamp: number) => {
  if (process.env.NODE_ENV !== "test")
    throw new Error(
      "setLastUserInteractionTimestamp() is only meant for use in tests"
    );

  lastUserInteractionTimestamp = timestamp;
};

export const resetLastUserInteractionTimestamp = () => {
  if (process.env.NODE_ENV !== "test")
    throw new Error(
      "resetLastUserInteractionTimestamp() is only meant for use in tests"
    );

  lastUserInteractionTimestamp = Date.now();
};
